import type { WegicRequestConfig } from '@wegic/request';
import { setStablePreviewUrl } from '@/helpers/preview/helper';
import { getVersionTimeline } from '@/services/project';
import { usePreviewStore } from '@/stores/preview';
import { useProjectStore } from '@/stores/project';

let stablePreviewRevision = 0;
let authoritativeStablePreview: {
  projectId: string;
  previewUrl: string;
  revision: number;
} | undefined;

export interface ApplyStablePreviewUrlOptions {
  projectId?: string;
  authoritative?: boolean;
}

export function applyStablePreviewUrl(
  previewUrl: string,
  options: ApplyStablePreviewUrlOptions = {},
): boolean {
  const normalizedUrl = previewUrl.trim();
  if (!normalizedUrl) {
    return false;
  }

  const previewState = usePreviewStore.getState();
  if (previewState.deliveryExpectedVersionId) {
    return normalizedUrl === previewState.preReleaseUrl;
  }
  stablePreviewRevision += 1;
  if (options.authoritative && options.projectId) {
    authoritativeStablePreview = {
      projectId: options.projectId,
      previewUrl: normalizedUrl,
      revision: stablePreviewRevision,
    };
  } else if (authoritativeStablePreview?.previewUrl !== normalizedUrl) {
    authoritativeStablePreview = undefined;
  }
  if (previewState.preReleaseUrl !== normalizedUrl) {
    previewState.setPreReleaseIframeReady(false);
    setStablePreviewUrl(normalizedUrl);
  }
  previewState.setPreReleaseReady(true);
  return true;
}

export async function getLatestVersionPreviewUrl(projectId: string, config?: WegicRequestConfig): Promise<string> {
  if (!projectId) {
    return '';
  }

  try {
    const data = await getVersionTimeline(projectId, { limit: 1 }, config);
    return data.list.find(item => item.previewUrl?.trim())?.previewUrl.trim() || '';
  } catch {
    return '';
  }
}

export async function syncLatestStablePreview(projectId: string): Promise<boolean> {
  const revisionAtRequest = stablePreviewRevision;
  const currentPreviewUrl = usePreviewStore.getState().preReleaseUrl;
  if (authoritativeStablePreview && currentPreviewUrl !== authoritativeStablePreview.previewUrl) {
    authoritativeStablePreview = undefined;
  }
  const previewUrl = await getLatestVersionPreviewUrl(projectId);
  if (
    !previewUrl ||
    useProjectStore.getState().projectId !== projectId ||
    stablePreviewRevision !== revisionAtRequest
  ) {
    return false;
  }

  const normalizedUrl = previewUrl.trim();
  // timeline 可能因最终一致性暂时落后于构建状态；已确认的新 URL 不能被旧数据覆盖。
  if (
    authoritativeStablePreview?.projectId === projectId
    && authoritativeStablePreview.revision >= revisionAtRequest
    && authoritativeStablePreview.previewUrl !== normalizedUrl
  ) {
    return false;
  }

  if (usePreviewStore.getState().preReleaseUrl === normalizedUrl) {
    return true;
  }

  return applyStablePreviewUrl(normalizedUrl);
}
